import React from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import Layout from './components/layout/Layout';
import MinimalLayout from './components/layout/MinimalLayout';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/auth/ProtectedRoute';
import Hero from './components/home/Hero';
import MusicLessons from './components/home/MusicLessons';
import Features from './components/home/Features';
import Testimonials from './components/home/Testimonials';
import TeacherSignUpPage from './pages/TeacherSignUpPage';
import InstrumentSelectionPage from './pages/InstrumentSelectionPage';
import LearnerQuestionnairePage from './pages/LearnerQuestionnairePage';
import AccountCreationPage from './pages/AccountCreationPage';
import AboutPage from './pages/AboutPage';
import ForTeachersPage from './pages/ForTeachersPage';
import PricingPage from './pages/PricingPage';
import ContactPage from './pages/ContactPage';
import HelpCenterPage from './pages/HelpCenterPage';
import ConfirmationPage from './pages/ConfirmationPage';
import EmailVerificationPage from './pages/EmailVerificationPage';
import TeacherPhotoSetupPage from './pages/TeacherPhotoSetupPage';
import TeacherProfileCompletePage from './pages/TeacherProfileCompletePage';
import TeacherQuestionnairePage from './pages/TeacherQuestionnairePage';
import TeacherProfilePage from './pages/TeacherProfilePage';
import StudentProfilePage from './pages/StudentProfilePage';
import BrowseTeachersPage from './pages/BrowseTeachersPage';
import TeacherDetailPage from './pages/TeacherDetailPage';
import BookLessonPage from './pages/BookLessonPage';
import BookingConfirmationPage from './pages/BookingConfirmationPage';
import TeacherDashboardPage from './pages/TeacherDashboardPage';
import StudentDashboardPage from './pages/StudentDashboardPage';
import MessagingPage from './pages/MessagingPage';
import PaymentPage from './pages/PaymentPage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import CalendarDemoPage from './pages/CalendarDemoPage';
import AdminDashboard from './pages/AdminDashboard';
import LoginPage from './pages/LoginPage';
import UnauthorizedPage from './pages/UnauthorizedPage';

// Page components for standard layout
const HomePageContent = () => (
  <>
    <Hero />
    <MusicLessons />
    <Features />
    <Testimonials />
  </>
);

const ServicesPageContent = () => <Features />;
const ContactPageContent = () => (
  <section className="py-16 bg-white">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center">
        <h2 className="text-3xl font-bold text-gray-900 mb-4">Get in Touch</h2>
        <p className="text-lg text-gray-600 mb-8">
          Have questions? We'd love to hear from you.
        </p>
      </div>
      {/* Contact form will be added here */}
    </div>
  </section>
);

// Login page is now imported from ./pages/LoginPage

// Main App component that switches layouts
const App: React.FC = () => {
  const location = useLocation();
  console.log('Rendering App component, current path:', location.pathname);

  const minimalLayoutRoutes = [
    '/become-a-teacher', 
    '/login', 
    '/instrument-selection',
    '/new-student/questions',
    '/account-creation',
    '/about-us',
    '/for-teachers',
    '/pricing',
    '/contact',
    '/help',
    '/confirm-details',
    '/teacher-questionnaire',
    '/teacher-profile',
    '/email-verification',
    '/teacher-photo-setup',
    '/teacher-profile-complete',
    '/student-profile',
    '/browse-teachers',
    '/teacher-detail',
    '/book-lesson',
    '/booking-confirmation',
    '/teacher-dashboard',
    '/messages',
    '/payment',
    '/payment-success',
    '/calendar-demo',
    '/admin'
  ];

  // Determine which layout to use
  const CurrentLayout = minimalLayoutRoutes.some(route => location.pathname.startsWith(route)) ? MinimalLayout : Layout;

  return (
    <AuthProvider>
      <CurrentLayout>
        <Routes>
          <Route path="/" element={<HomePageContent />} />
          <Route path="/services" element={<ServicesPageContent />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/help" element={<HelpCenterPage />} />
          <Route path="/become-a-teacher" element={<TeacherSignUpPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/unauthorized" element={<UnauthorizedPage />} />
          <Route path="/instrument-selection" element={<InstrumentSelectionPage />} />
          <Route path="/new-student/questions" element={<LearnerQuestionnairePage />} />
          <Route path="/account-creation" element={<AccountCreationPage />} />
          <Route path="/about-us" element={<AboutPage />} />
          <Route path="/for-teachers" element={<ForTeachersPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/confirm-details" element={<ConfirmationPage />} />
          <Route path="/teacher-questionnaire" element={<TeacherQuestionnairePage />} />
          <Route path="/teacher-profile" element={<TeacherProfilePage />} />
          <Route path="/email-verification" element={<EmailVerificationPage />} />
          <Route path="/teacher-photo-setup" element={<TeacherPhotoSetupPage />} />
          <Route path="/teacher-profile-complete" element={<TeacherProfileCompletePage />} />
          <Route path="/student-profile" element={<StudentProfilePage />} />
          <Route path="/browse-teachers" element={<BrowseTeachersPage />} />
          <Route path="/teacher-detail" element={<TeacherDetailPage />} />
          <Route path="/book-lesson" element={<BookLessonPage />} />
          <Route path="/booking-confirmation" element={<BookingConfirmationPage />} />
          
          {/* Protected Routes */}
          <Route path="/teacher-dashboard" element={
            <ProtectedRoute requiredRole="teacher">
              <TeacherDashboardPage />
            </ProtectedRoute>
          } />
          <Route path="/student-dashboard" element={
            <ProtectedRoute requiredRole="student">
              <StudentDashboardPage />
            </ProtectedRoute>
          } />
          <Route path="/messages" element={
            <ProtectedRoute allowedRoles={['student', 'teacher']}>
              <MessagingPage />
            </ProtectedRoute>
          } />
          <Route path="/payment" element={
            <ProtectedRoute allowedRoles={['student', 'teacher']}>
              <PaymentPage />
            </ProtectedRoute>
          } />
          <Route path="/payment-success" element={
            <ProtectedRoute allowedRoles={['student', 'teacher']}>
              <PaymentSuccessPage />
            </ProtectedRoute>
          } />
          <Route path="/calendar-demo" element={<CalendarDemoPage />} />
          <Route path="/admin" element={
            <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
              <AdminDashboard />
            </ProtectedRoute>
          } />
        </Routes>
      </CurrentLayout>
    </AuthProvider>
  );
};

export default App; 